import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, signal } from '@angular/core';
import { StorageKeys } from '../constants/storage-keys';
import { AppLanguage, translations } from '../i18n/translations';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly languageSignal = signal<AppLanguage>(this.readInitialLanguage());

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.applyDocumentState(this.languageSignal());
  }

  currentLanguage(): AppLanguage {
    return this.languageSignal();
  }

  direction(): 'ltr' | 'rtl' {
    return this.languageSignal() === 'ar' ? 'rtl' : 'ltr';
  }

  isRtl(): boolean {
    return this.direction() === 'rtl';
  }

  setLanguage(language: AppLanguage): void {
    this.languageSignal.set(language);
    localStorage.setItem(StorageKeys.language, language);
    this.applyDocumentState(language);
  }

  t(key: string, params?: Record<string, string | number>): string {
    const language = this.languageSignal();
    const template = translations[language][key] ?? translations.en[key] ?? key;

    if (!params) {
      return template;
    }

    return Object.entries(params).reduce(
      (value, [paramKey, paramValue]) => value.replaceAll(`{${paramKey}}`, String(paramValue)),
      template
    );
  }

  private readInitialLanguage(): AppLanguage {
    const saved = localStorage.getItem(StorageKeys.language);
    return saved === 'ar' ? 'ar' : 'en';
  }

  private applyDocumentState(language: AppLanguage): void {
    const direction = language === 'ar' ? 'rtl' : 'ltr';
    this.document.documentElement.lang = language;
    this.document.documentElement.dir = direction;
    this.document.body.dir = direction;
  }
}
